/* Xiaomi 2026 · Partner Directory CSV Export modal
 *
 * Opens from the Partner Directory header.  Shows a grouped checkbox
 * grid (Identity / Contact / Personal / Xiaomi chain / Concierge /
 * 2025 history / 2026 plan / Admin), with "Select all" / "Lean preset"
 * / "Clear" shortcuts and a row count preview.
 *
 * The download honours every filter currently set on the directory
 * (tier, zone, language, state, search, due_month) — passed in via
 * the `filters` prop.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});


export default function CsvExportModal({ filters, totalCount, onClose }) {
  const [registry, setRegistry] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  // Load column registry once
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/xiaomi/dealers/export-columns`, auth());
        setRegistry(r.data);
        setSelected(new Set(r.data.presets.lean));   // sensible default
      } catch (e) {
        toast.error('Could not load column list');
      }
    })();
  }, []);

  const allColumns = useMemo(() => {
    if (!registry) return [];
    return Object.values(registry.groups).flat().map((c) => c.key);
  }, [registry]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allColumns));
  const selectLean = () => setSelected(new Set(registry?.presets?.lean || []));
  const clearAll = () => setSelected(new Set());

  const buildUrl = () => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v && String(v).length > 0) params.set(k, String(v));
    });
    // If user selected EVERY column, pass "all" so the URL stays short
    if (selected.size === allColumns.length) {
      params.set('columns', 'all');
    } else if (selected.size > 0) {
      params.set('columns', Array.from(selected).join(','));
    }
    return `${API}/xiaomi/dealers/export.csv?${params.toString()}`;
  };

  const download = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one column');
      return;
    }
    setDownloading(true);
    try {
      const r = await axios.get(buildUrl(), {
        ...auth(), responseType: 'blob',
      });
      // Derive filename from server's Content-Disposition
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'xiaomi_dealers.csv';
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const rowCount = r.headers['x-row-count'] || '?';
      toast.success(`Downloaded ${filename} · ${rowCount} rows`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally { setDownloading(false); }
  };

  if (!registry) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 text-sm">Loading columns…</div>
      </div>
    );
  }

  const activeFilterChips = Object.entries(filters || {})
    .filter(([_k, v]) => v && String(v).length > 0)
    .map(([k, v]) => `${k}=${v}`);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="csv-export-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-start justify-between"
          style={{ background: 'linear-gradient(135deg,#fff7ed,#ffedd5)' }}>
          <div>
            <h2 className="text-xl" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
              Export partners to CSV
            </h2>
            <p className="text-[12px] text-soft mt-1">
              <strong>{totalCount || 'all'}</strong> partner{totalCount === 1 ? '' : 's'} match the current filters
              {activeFilterChips.length > 0 && (
                <>
                  {' · '}
                  {activeFilterChips.map((c) => (
                    <span key={c} className="inline-block px-1.5 py-0.5 rounded bg-white text-[#7c2d12] border border-[#fed7aa] mx-0.5 text-[10px] font-mono">
                      {c}
                    </span>
                  ))}
                </>
              )}
            </p>
          </div>
          <button onClick={onClose}
            className="text-soft hover:text-ink text-2xl"
            data-testid="csv-export-close">×</button>
        </div>

        {/* Toolbar · presets */}
        <div className="px-6 py-3 border-b border-line flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-soft">Quick pick</span>
          <button onClick={selectLean}
            className="px-3 py-1 rounded-full text-[11px] font-semibold bg-[#fff7ed] text-[#7c2d12] border border-[#fb923c] hover:bg-[#ffedd5]"
            data-testid="csv-preset-lean">
            Lean · 15 cols
          </button>
          <button onClick={selectAll}
            className="px-3 py-1 rounded-full text-[11px] font-semibold bg-[#15110d] text-white hover:bg-[#2a2419]"
            data-testid="csv-preset-all">
            Select all · {allColumns.length} cols
          </button>
          <button onClick={clearAll}
            className="px-3 py-1 rounded-full text-[11px] font-semibold text-soft border border-line hover:bg-[#faf6ef]"
            data-testid="csv-preset-clear">
            Clear
          </button>
          <span className="ml-auto text-[11px] text-[#15110d]"
            data-testid="csv-selected-count">
            <strong>{selected.size}</strong> / {allColumns.length} selected
          </span>
        </div>

        {/* Grouped checkboxes */}
        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          {Object.entries(registry.groups).map(([group, cols]) => {
            const groupKeys = cols.map((c) => c.key);
            const groupSelectedCount = groupKeys.filter((k) => selected.has(k)).length;
            const allChecked = groupSelectedCount === groupKeys.length;
            return (
              <div key={group} className="border border-line rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[12px] uppercase tracking-wider text-soft font-bold">
                    {group}
                  </h3>
                  <button
                    onClick={() => {
                      const next = new Set(selected);
                      if (allChecked) groupKeys.forEach((k) => next.delete(k));
                      else groupKeys.forEach((k) => next.add(k));
                      setSelected(next);
                    }}
                    className="text-[10px] underline text-soft hover:text-[#7c2d12]"
                    data-testid={`csv-group-toggle-${group.replace(/[^a-z0-9]/gi,'-')}`}
                  >
                    {allChecked ? 'deselect all' : 'select all'}
                    {' '}({groupSelectedCount}/{groupKeys.length})
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {cols.map((c) => (
                    <label key={c.key}
                      className="flex items-start gap-1.5 cursor-pointer hover:bg-[#faf6ef] rounded px-1 py-0.5"
                      data-testid={`csv-col-${c.key.replace(/\./g,'-')}`}>
                      <input type="checkbox" checked={selected.has(c.key)}
                        onChange={() => toggle(c.key)}
                        className="mt-0.5" />
                      <div>
                        <div className="text-[12px] text-[#15110d] font-medium leading-tight">
                          {c.label}
                        </div>
                        <div className="text-[9px] text-soft font-mono">
                          {c.key}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer · download */}
        <div className="px-6 py-3 border-t border-line flex justify-end gap-2">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg border border-line text-sm">
            Cancel
          </button>
          <button onClick={download} disabled={downloading || selected.size === 0}
            className="px-6 py-2 rounded-lg bg-[#15803d] text-white font-semibold text-sm hover:bg-[#166534] disabled:opacity-50"
            data-testid="csv-download-btn">
            {downloading
              ? 'Generating…'
              : `⬇ Download CSV (${selected.size} cols)`}
          </button>
        </div>
      </div>
    </div>
  );
}
